import {Component, DOCUMENT, Inject, inject, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {Location} from '@angular/common';
import {MatIconModule} from '@angular/material/icon';
import {MatDivider} from '@angular/material/divider';
import {MemoryService} from '../../../../core/services/memory.service';
import {LocationQueryDto} from '../../../startup/dtos/location-query.dto';
import {LocationDetailDto} from '../../../startup/dtos/location-detail.dto';
import {LocationService} from '../../../../core/services/location.service';
import {ConfirmState} from '../../models/confirmstate';
import {GroupSeriesInfoDto} from '../../dtos/groupseriesinfo.dto';
import {GrouAppointmentModel} from '../../models/groupappointmentmodel';
import {BreakpointObserver} from '@angular/cdk/layout';
import {LogoComponent} from '../../../../shared/components/logo/logo.component';
import {TextButton} from '../../../../shared/components/text-button/text-button.component';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {DateTime} from 'luxon';
import {GroupAppointmentInfoDto} from '../../dtos/groupappointmentinfo.dto';
import {take} from 'rxjs';
import {MatFormFieldModule} from '@angular/material/form-field';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {MatInputModule} from '@angular/material/input';
import {ErrorService} from '../../../../core/services/error.service';
import {BookGroupsDto} from '../../dtos/bookgroups.dto';
import {CourseService} from '../../course.service';
import {AppointmentResponseCode} from '../../../booking/enums/appointment-response-code.enum';
import {GroupBookingResponseDto} from '../../dtos/group-booking-response.dto';
import {BookingResults} from '../../models/bookingresult';
import {RedirectService} from '../../../../core/services/redirect.service';

@Component({
  selector: 'app-course-confirm',
  imports: [LogoComponent, MatDivider, MatIconModule, TextButton, TranslocoPipe, MatInputModule, MatFormFieldModule, MatFormFieldModule, ReactiveFormsModule],
  templateUrl: './course-confirm.component.html',
  styleUrl: './course-confirm.component.scss'
})
export class CourseConfirmComponent implements OnInit {
  router: Router = inject(Router);
  memoryService = inject(MemoryService);
  locationService = inject(LocationService);
  translocoService = inject(TranslocoService);
  errorService = inject(ErrorService);
  courseService = inject(CourseService);
  redirectService = inject(RedirectService);
  course!: GroupSeriesInfoDto;
  selectedAppointments: GrouAppointmentModel[];
  mode: 'mobile' | 'tablet' | 'desktop' = 'desktop';
  bookingMessageForm: FormControl = new FormControl("");
  errors: string[] = [];

  constructor(@Inject(DOCUMENT) private document: Document, private location: Location, private breakpointObserver: BreakpointObserver) {
    this.breakpointObserver.observe([
      '(max-width: 980px)',     // mobile
      '(min-width: 981px) and (max-width: 999px)', // tablet
      '(min-width: 1000px)'      // desktop
    ]).subscribe(result => {
      if (result.breakpoints['(max-width: 980px)']) {
        this.mode = 'mobile';
      } else if (result.breakpoints['(min-width: 981px) and (max-width: 999px)']) {
        this.mode = 'tablet';
      } else if (result.breakpoints['(min-width: 1000px)']) {
        this.mode = 'desktop';
      }
    });
    var data = this.location.getState() as ConfirmState;
    this.course = data.course!;
    this.selectedAppointments = data.selectedAppointments!;
  }

  ngOnInit() {
    this.translocoService.selectTranslation().pipe(take(1)).subscribe(() => {
      this.initializeLocation();
    });
  }

  initializeLocation() {
    const locationId = this.memoryService.locationId;
    if (!locationId) {
      return;
    }

    const locationQueryDto: LocationQueryDto = {customerId: this.memoryService.customerId!, locationIds: [locationId]};
    this.locationService.queryLocationDetails(locationQueryDto).subscribe({
      next: (locationDetailDtos: LocationDetailDto[]) => {
        const locationDetailDto = locationDetailDtos.find(l => l.locationId == locationId);
        if (!locationDetailDto) {
          return;
        }

        this.setProperty("--custom-primary-color", locationDetailDto.appearanceSettings.primaryColor);
        this.setProperty("--custom-secondary-color", locationDetailDto.appearanceSettings.secondaryColor);
        this.setProperty("--custom-tertiary-color", locationDetailDto.appearanceSettings.tertiaryColor);
        this.memoryService.locationDetailDto = locationDetailDto;
      }
    });
  }

  locationInfo() {
    return this.memoryService.locationDetailDto?.name + ', ' + this.memoryService.locationDetailDto?.contact?.street + ', ' + this.memoryService.locationDetailDto?.contact?.city;
  }

  getFormatedDate(input: GroupAppointmentInfoDto) {
    if (!input) {
      return "-";
    }

    var startTime = DateTime.fromISO(input?.start!);
    var endTime = DateTime.fromISO(input?.end!);
    const weekday = startTime.setLocale(this.translocoService.getActiveLang()).toFormat('EEE');
    const date = startTime.toFormat('dd.MM.yyyy');
    return `${weekday} ${date} ${startTime.toFormat('HH:mm')} - ${endTime.toFormat('HH:mm')}`;
  }

  book() {
    const patientId = this.memoryService.patientId;
    if (!patientId) {
      const url = this.router.url;
      const state = history.state;
      this.redirectService.set({url: url, state: state});

      this.router.navigate(["/login"]);
      return;
    }

    const dto: BookGroupsDto = {
      patientId: this.memoryService.patientId!,
      locationId: this.memoryService.locationId!,
      courseId: this.course.id,
      message: this.bookingMessageForm.value,
      appointmentIds: this.selectedAppointments.map(p => p.dto?.id!)
    }
    this.courseService.bookCourses(dto).subscribe({
      next: (appointmentResponseDtos: GroupBookingResponseDto[]) => {
        this.errors = [];
        if (!appointmentResponseDtos || appointmentResponseDtos.length == 0) {
          this.errors.push(this.translocoService.translate("error.unexpected"));
          return;
        }

        let bookingResults: BookingResults[] = [];
        appointmentResponseDtos.forEach(dto => {
          var error = '';
          switch (dto.appointmentResponseCode) {
            case AppointmentResponseCode.Ok:
              break;
            case AppointmentResponseCode.MaxAppointmentsPerWeekExceeded:
              error = this.translocoService.translate("booking.error.maxAppointmentsPerWeekExceeded");
              break;
            case AppointmentResponseCode.MinFutureBookingHoursNotMet:
              error = this.translocoService.translate("booking.error.minFutureBookingHoursNotMet");
              break;
            case AppointmentResponseCode.MaxAppointmentFrequencyExceeded:
              error = this.translocoService.translate("booking.error.maxAppointmentFrequencyExceeded");
              break;
            case AppointmentResponseCode.GroupAppointmentBookedOut:
              error = this.translocoService.translate("booking.error.groupAppointmentBookedOut");
              break;
            case AppointmentResponseCode.PatientNotFound:
              error = this.translocoService.translate("booking.error.patientNotFound");
              break;
            case AppointmentResponseCode.PatientNotAllowed:
              error = this.translocoService.translate("booking.error.patientNotAllowed");
              break;
            case AppointmentResponseCode.AboMissing:
              error = this.translocoService.translate("booking.error.aboMissing");
              break;
            case AppointmentResponseCode.EmployeeBookingConstraintsNotMet:
            case AppointmentResponseCode.CategoryBookingConstraintsNotMet:
            case AppointmentResponseCode.TimeSlotNotAvailable:
              error = this.translocoService.translate("booking.error.timeSlotNotAvailable");
              break;
            default:
              error = this.translocoService.translate("booking.error.unexpected");
          }

          var found = this.selectedAppointments.find(p => p.dto!.id == dto.appointmentGroupId);
          bookingResults.push(new BookingResults(dto.appointmentGroupId, this.getFormatedDate(found!.dto!), error, error == ''));
        });

        this.router.navigate(['/course-success'], {state: {course: this.course, bookingResults: bookingResults}});
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    })
  }

  cancel() {
    this.router.navigate(['courses']);
  }

  back() {
    this.location.back();
  }

  private setProperty(propertyName: string, value?: string) {
    if (!value) {
      return;
    }

    this.document.documentElement.style.setProperty(propertyName, value)
  }
}
